import axios from '../axios';
import type { Pagination } from '../interfaces/api';
import { Device } from '../interfaces/devices';
import type { PlayHistoryObject } from '../interfaces/player';

/**
 * @description Get information about the user’s current playback state, including track or episode, progress, and active device.
 */
const fetchPlaybackState = async () => {
  const startTime = Date.now();
  console.info('[DeviceInitTiming] fetchPlaybackState API call start', {
    timestamp: new Date().toISOString(),
    url: '/me/player',
  });
  try {
    const response = await axios.get('/me/player');
    const duration = Date.now() - startTime;
    console.info('[DeviceInitTiming] fetchPlaybackState API call complete', {
      timestamp: new Date().toISOString(),
      durationMs: duration,
      status: response.status,
      hasData: !!response.data,
    });
    return response.data;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error('[DeviceInitTiming] fetchPlaybackState API call failed', {
      timestamp: new Date().toISOString(),
      durationMs: duration,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 *
 * @description Transfer playback to a new device and optionally begin playback. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 * @param deviceId The ID of the device this command is targeting. If not supplied, the user’s currently active device is the target.
 */
const transferPlayback = async (deviceId: string, shouldPlay: boolean = false) => {
  const startTime = Date.now();
  const perfStartMark = `transfer-start-${Date.now()}`;
  if (typeof performance !== 'undefined' && performance.mark) {
    performance.mark(perfStartMark);
  }
  
  console.info('[DeviceInitTiming] transferPlayback API call start', {
    timestamp: new Date().toISOString(),
    url: '/me/player',
    deviceId,
    shouldPlay,
  });
  try {
    // Add timeout to prevent hanging on 404 errors (device not found)
    const timeoutSetupTime = Date.now();
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Transfer timeout after 5 seconds')), 5000);
    });
    const timeoutSetupDuration = Date.now() - timeoutSetupTime;
    
    // Use requestAnimationFrame to check if event loop is blocked BEFORE axios call
    // If this takes >16ms, something is blocking the event loop
    const rafStartTime = performance.now();
    await new Promise(resolve => requestAnimationFrame(resolve));
    const rafDuration = performance.now() - rafStartTime;
    
    const axiosCallStartTime = Date.now();
    const axiosCallStartMark = `transfer-axios-call-${Date.now()}`;
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(axiosCallStartMark);
    }
    
    const axiosPromise = axios.put('/me/player', { device_ids: [deviceId], play: shouldPlay });
    const axiosCallSetupDuration = Date.now() - axiosCallStartTime;
    
    const raceStartTime = Date.now();
    const raceStartMark = `transfer-race-start-${Date.now()}`;
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(raceStartMark);
    }
    
    await Promise.race([axiosPromise, timeoutPromise]);
    const raceDuration = Date.now() - raceStartTime;
    
    const duration = Date.now() - startTime;
    
    // Measure time between axios call and when promise resolves
    if (typeof performance !== 'undefined' && performance.measure) {
      try {
        performance.measure('transfer-axios-to-complete', axiosCallStartMark, raceStartMark);
        const measures = performance.getEntriesByName('transfer-axios-to-complete');
        if (measures.length > 0) {
          console.info('[DeviceInitTiming] transferPlayback performance measure', {
            duration: measures[0].duration,
            startTime: measures[0].startTime,
          });
        }
      } catch (e) {
        // Ignore measurement errors
      }
    }
    
    console.info('[DeviceInitTiming] transferPlayback API call complete', {
      timestamp: new Date().toISOString(),
      durationMs: duration,
      timeoutSetupMs: timeoutSetupDuration,
      axiosSetupMs: axiosCallSetupDuration,
      rafDelayMs: rafDuration, // If >16ms, event loop is blocked
      raceDurationMs: raceDuration,
      deviceId,
      shouldPlay,
    });
  } catch (error) {
    const duration = Date.now() - startTime;
    const errorMessage = error instanceof Error ? error.message : String(error);
    const is404 = (error as any)?.response?.status === 404;
    
    // 404 means device doesn't exist yet - this is expected during initialization
    // Don't log as error, just info, and don't throw - let initialization continue
    if (is404) {
      console.info('[DeviceInitTiming] transferPlayback API call - device not found (expected during init)', {
        timestamp: new Date().toISOString(),
        durationMs: duration,
        deviceId,
        shouldPlay,
      });
      return; // Silently continue - device will be available soon
    }
    
    console.error('[DeviceInitTiming] transferPlayback API call failed', {
      timestamp: new Date().toISOString(),
      durationMs: duration,
      deviceId,
      shouldPlay,
      error: errorMessage,
    });
    throw error;
  }
};

/**
 * @description Get information about a user’s available Spotify Connect devices. Some device models are not supported and will not be listed in the API response.
 */
const getAvailableDevices = async () => {
  const startTime = Date.now();
  console.info('[DeviceInitTiming] getAvailableDevices API call start', {
    timestamp: new Date().toISOString(),
    url: '/me/player/devices',
  });
  try {
    const response = await axios.get<{ devices: Device[] }>('/me/player/devices');
    const duration = Date.now() - startTime;
    console.info('[DeviceInitTiming] getAvailableDevices API call complete', {
      timestamp: new Date().toISOString(),
      durationMs: duration,
      status: response.status,
      deviceCount: Array.isArray(response.data?.devices) ? response.data.devices.length : 0,
    });
    return response.data;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error('[DeviceInitTiming] getAvailableDevices API call failed', {
      timestamp: new Date().toISOString(),
      durationMs: duration,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * @description Start a new context or resume current playback on the user's active device. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 */
const startPlayback = async (
  body: {
    context_uri?: string;
    uris?: string[];
    offset?: { position: number };
    position_ms?: number;
  } = {},
  deviceId?: string
) => {
  const authHeader = axios.defaults.headers.common['Authorization'];
  console.log('[SpotifyTokenTrace] axios.startPlayback request', {
    url: '/me/player/play',
    deviceId: deviceId ?? null,
    body,
    authorizationHeader: typeof authHeader === 'string' ? authHeader : null,
    withCredentials: true
  });
  await axios.put(
    '/me/player/play',
    body,
    deviceId ? { params: { device_id: deviceId } } : undefined
  );
};

/**
 * @description Pause playback on the user's account. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 */
const pausePlayback = async (deviceId?: string) => {
  const authHeader = axios.defaults.headers.common['Authorization'];
  console.log('[SpotifyTokenTrace] axios.pausePlayback request', {
    url: '/me/player/pause',
    deviceId: deviceId ?? null,
    authorizationHeader: typeof authHeader === 'string' ? authHeader : null,
    withCredentials: true
  });
  await axios.put('/me/player/pause', {}, deviceId ? { params: { device_id: deviceId } } : undefined);
};

/**
 * @description Skip to the next track in the user’s queue. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 */
const nextTrack = async () => {
  await axios.post('/me/player/next');
};

/**
 * @description Skip to the previous track in the user’s queue. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 */
const previousTrack = async () => {
  await axios.post('/me/player/previous');
};

/**
 * @description Seeks to the given position in the user’s currently playing track. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 */
const seekToPosition = async (position_ms: number) => {
  await axios.put('/me/player/seek', {}, { params: { position_ms } });
};

/**
 * @description Set the repeat mode for the user's playback. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 * @param state track, context, or off. track will repeat the current track. context will repeat the current context. off will turn repeat off.
 */
const setRepeatMode = async (state: 'track' | 'context' | 'off') => {
  await axios.put('/me/player/repeat', {}, { params: { state } });
};

/**
 * @description Set the volume for the user’s current playback device. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 * @param volume_percent The volume to set. Must be a value from 0 to 100 inclusive.
 */
const setVolume = async (volume_percent: number, deviceId?: string) => {
  await axios.put(
    '/me/player/volume',
    {},
    {
      params: deviceId ? { volume_percent, device_id: deviceId } : { volume_percent },
    }
  );
};

/**
 * @description Toggle shuffle on or off for user’s playback. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 */
const toggleShuffle = async (state: boolean) => {
  await axios.put('/me/player/shuffle', {}, { params: { state } });
};

/**
 * @description Add an item to the end of the user's current playback queue. This API only works for users who have Spotify Premium. The order of execution is not guaranteed when you use this API with other Player API endpoints.
 */
const addToQueue = async (uri: string) => {
  await axios.post('/me/player/queue', {}, { params: { uri } });
};

/**
 * @description Get tracks from the current user's recently played tracks. Note: Currently doesn't support podcast episodes.
 */
const getRecentlyPlayed = async (params: { limit?: number; after?: number; before?: number }) => {
  const response = await axios.get<Pagination<PlayHistoryObject>>('/me/player/recently-played', {
    params,
  });
  return response.data;
};

/**
 * @description Start playback of a specific track
 */
const resumePlayback = async (deviceId?: string): Promise<void> => {
  await startPlayback({}, deviceId);
};

export const playerService = {
  addToQueue,
  fetchPlaybackState,
  transferPlayback,
  startPlayback,
  pausePlayback,
  resumePlayback,
  nextTrack,
  previousTrack,
  setRepeatMode,
  setVolume,
  toggleShuffle,
  seekToPosition,
  getRecentlyPlayed,
  getAvailableDevices,
};
